import { Menu } from '@shared/models';
import { RegionAvailable } from '@shared/services/language/registry';
import { JsonLD } from '@shared/services/seo/models';
import { envConfiguredHostname, normalizeUrlPath } from '@shared/utils';

/**
 * Builds a JSON-LD object representing the site navigation structure for SEO purposes.
 *
 * This function generates a structured JSON-LD `SiteNavigationElement` object,
 * embedding details about categories and quick links provided in the menu data.
 *
 * @param {string} name - Name of the navigation element, typically the site or section name.
 * @param {Partial<Menu> | null | undefined} menu - Navigation menu data, containing categories and quick links
 * that can include their labels, URLs, or sub-items.
 * @returns {JsonLD} A JSON-LD object conforming to the `SiteNavigationElement` schema.
 *
 * @see MenuComponent and MenuBlogComponent, its callers.
 */
export const buildNavigationJsonLd = (
  name: string,
  menu: Partial<Menu> | null | undefined,
  region: RegionAvailable
): JsonLD => {
  const base = envConfiguredHostname(region);
  const toAbs = (path: string) => normalizeUrlPath(`${base}${path}`);

  const toPage = (label: string, url: string): JsonLD | null => {
    if (!url || url === '/') return null;
    return { '@type': 'WebPage', name: label, url: toAbs(url) };
  };
  const isPage = (p: JsonLD | null): p is JsonLD => p !== null;

  const categoryPages: JsonLD[] = (menu?.categories ?? []).flatMap((c) => {
    const pages: JsonLD[] = [];
    if ('url' in c && c.url && c.url !== '/') {
      const parent = toPage(c.label, c.url);
      if (parent) pages.push(parent);
    }
    if ('subItems' in c) {
      pages.push(...c.subItems.map((s) => toPage(s.label, s.url)).filter(isPage));
    }
    return pages;
  });

  const quickLinkPages: JsonLD[] = (menu?.quickLinks ?? []).map((l) => toPage(l.label, l.url)).filter(isPage);

  return {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name,
    mainEntity: [...categoryPages, ...quickLinkPages],
  };
};
